// frontend/src/lib/stripe.js
//
// Thin client for the Stripe-backed billing endpoints exposed by our
// FastAPI server. We do *not* use @stripe/stripe-js on the client
// because Checkout is fully hosted by Stripe — the only thing the
// browser needs to do is POST a request to our API and follow the
// returned `url`.
//
// Public surface
// ──────────────
//   getBillingConfig()          → { stripe_enabled, subscription_enabled,
//                                   donation_enabled, tax_enabled,
//                                   currencies, donation_min }
//   startSubscription(interval) → redirects the browser to Stripe Checkout
//   openCustomerPortal()        → redirects the browser to Stripe Portal
//   startDonation({...})        → redirects the browser to Stripe Checkout
//   getCheckoutSession(id)      → fetch sanitised session for the Thanks page
//
// Behaviour
// ─────────
//   • A 5-second in-memory cache for /billing/config keeps the page snappy
//     without spamming Stripe-config polls.
//   • All errors normalise to {code, message, status} so the UI can render
//     localised copy (see billingErrorMessage below).
//   • The donation flow is anonymous-friendly — Stripe Checkout collects
//     the email on the hosted page when the donor isn't logged in.

import { api, apiBase } from './api';

let _configCache = null;
let _configCacheAt = 0;
const CONFIG_TTL_MS = 5_000;

/** Fetch /billing/config, with a tiny TTL cache. Always resolves; on
 *  network failure returns a "disabled" shape so the UI hides buttons. */
export async function getBillingConfig() {
  const now = Date.now();
  if (_configCache && (now - _configCacheAt) < CONFIG_TTL_MS) {
    return _configCache;
  }
  try {
    const cfg = await api('/api/billing/config');
    _configCache = cfg;
    _configCacheAt = now;
    return cfg;
  } catch (_) {
    // Fail closed: never accidentally show payment buttons that 503.
    return {
      stripe_enabled: false,
      subscription_enabled: false,
      donation_enabled: false,
      tax_enabled: false,
      currencies: ['brl', 'usd'],
      donation_min: { brl: 500, usd: 100 },
    };
  }
}

/** Force-refresh the cached config (used after admin saves new keys). */
export function clearBillingConfigCache() {
  _configCache = null;
  _configCacheAt = 0;
}

/** Start a Pro subscription — redirects to Stripe Checkout.
 *  Throws when the user isn't logged in or Stripe isn't configured. */
export async function startSubscription(interval = 'yearly') {
  const r = await api(`/api/billing/checkout?interval=${encodeURIComponent(interval)}`,
    { method: 'POST' });
  if (!r?.url) throw new Error('No checkout URL returned');
  window.location.assign(r.url);
}

/** Open the Stripe Customer Portal — billing history, card change,
 *  cancel. Requires a customer record (i.e. at least one checkout). */
export async function openCustomerPortal() {
  const r = await api('/api/billing/portal', { method: 'POST' });
  if (!r?.url) throw new Error('No portal URL returned');
  window.location.assign(r.url);
}

/** Create a one-off donation Checkout Session and redirect.
 *
 *  @param {Object} opts
 *  @param {number}  opts.amount_cents  e.g. 1500 for R$15,00 / $15.00
 *  @param {'brl'|'usd'} opts.currency
 *  @param {string=} opts.name          optional donor display name
 *  @param {string=} opts.message       optional public message (≤200 chars)
 *  @param {boolean=} opts.anonymous    when true, name/message are stored
 *                                       but never exposed publicly — the
 *                                       Worker honours this flag on every
 *                                       row write AND on every public read.
 */
export async function startDonation({ amount_cents, currency, name, message, anonymous }) {
  if (!Number.isInteger(amount_cents) || amount_cents < 100) {
    throw new Error('amount_too_low');
  }
  const r = await api('/api/billing/donate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      amount_cents,
      currency: (currency || 'brl').toLowerCase(),
      name: name || null,
      message: message || null,
      anonymous: !!anonymous,
    }),
  });
  if (!r?.url) throw new Error('No checkout URL returned');
  window.location.assign(r.url);
}

/** Fetch a Checkout Session for the /thanks landing page. The session_id
 *  itself is the proof of ownership — no auth needed. */
export async function getCheckoutSession(sessionId) {
  if (!sessionId || !sessionId.startsWith('cs_')) {
    throw new Error('bad_session');
  }
  return api(`/api/billing/session/${encodeURIComponent(sessionId)}`);
}

// ─── Single-project download (one-off purchase) ──────────────────────
//
// Companion product to the subscription tiers: visitors who don't want
// to commit to a monthly plan can pay a small one-time fee (USD 1.49 /
// EUR 1.49 / BRL 3.90) to unlock a single DXF/SVG export of their exact
// tool parametrisation.
//
// Lifecycle:
//   1. Frontend builds the file client-side (same generators that paid
//      subscribers use), encodes it base64, calls startSinglePurchase().
//   2. Worker stores a pending row + creates Stripe Checkout Session,
//      returns its URL. Browser is redirected there.
//   3. After payment, Stripe redirects back to /thanks/single?session_id=…
//      That page polls getSinglePurchaseBySession() until status='succeeded'.
//   4. Page calls downloadSinglePurchase(token) which streams the bytes
//      back as a normal browser download.
//
// Stripe collects email on its hosted page (anonymous-friendly), so a
// non-logged-in visitor only needs to type their email once.

/** Pick the right one-off currency for the current user.
 *
 *  Rules:
 *    • Explicit override via opts.currency (lower-case 'usd'|'eur'|'brl')
 *      always wins — useful for testing or admin tooling.
 *    • Brazilian Portuguese visitors get BRL (R$ 3,90 — same price as
 *      our donations and what they're used to seeing on /pricing).
 *    • French / German / Italian / Spanish / Dutch get EUR (€ 1,49).
 *    • Everyone else (English, Chinese, Japanese, Arabic, fallback) → USD ($ 1.49).
 *
 *  We expose this as a pure helper so SinglePurchaseCard and the
 *  /thanks/single page both pick the same default without prop-drilling. */
export function pickSinglePurchaseCurrency({ lang, currency } = {}) {
  const explicit = (currency || '').toLowerCase();
  if (explicit === 'usd' || explicit === 'eur' || explicit === 'brl') return explicit;
  const l = (lang || '').toLowerCase();
  if (l === 'pt') return 'brl';
  if (['fr', 'de', 'it', 'es', 'nl'].includes(l)) return 'eur';
  return 'usd';
}

/** Amount in cents for each accepted currency. Mirrors the worker's
 *  SINGLE_PURCHASE_PRICES constant — keep them in sync. */
export const SINGLE_PURCHASE_AMOUNT_CENTS = {
  usd: 149,
  eur: 149,
  brl: 390,
};

/** Encode arbitrary file content (string or Uint8Array) to base64.
 *  Uses chunked Latin-1 → btoa() to avoid blowing the call stack on
 *  multi-MB payloads (typical DXF/SVG sit at 5-50 kB so this is just
 *  defensive). */
export function toBase64(content) {
  // Already a base64 string? Trust the caller.
  if (typeof content === 'string' && /^[A-Za-z0-9+/=\r\n]+$/.test(content) && content.length % 4 === 0) {
    // Heuristic: only trust as-is if it's *really* short or really clean.
    // The vast majority of our callers pass raw DXF/SVG text, so we
    // fall through to the encode path below for safety.
  }
  let bytes;
  if (typeof content === 'string') {
    // DXF and SVG are 7-bit ASCII; TextEncoder gives us UTF-8 which is
    // identical to ASCII for those payloads (and correct for any
    // accented filename comments).
    bytes = new TextEncoder().encode(content);
  } else if (content instanceof Uint8Array) {
    bytes = content;
  } else if (content instanceof ArrayBuffer) {
    bytes = new Uint8Array(content);
  } else {
    throw new Error('toBase64: unsupported content type');
  }
  // Chunked Latin-1 string build (avoid String.fromCharCode(...bytes) stack overflow)
  let binary = '';
  const CHUNK = 0x8000; // 32 KB
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/** Create a one-off single-project purchase Checkout Session and redirect.
 *
 *  @param {Object} opts
 *  @param {string} opts.toolSlug          e.g. 'sliding-lid', 'bend-calculator'
 *  @param {Object} opts.params            opaque JSON snapshot of tool params
 *  @param {'usd'|'eur'|'brl'} opts.currency
 *  @param {string} opts.fileName          e.g. 'sliding-lid-200x150x80.dxf'
 *  @param {string} opts.fileMime          e.g. 'image/vnd.dxf' or 'image/svg+xml'
 *  @param {string|Uint8Array} opts.content  raw file bytes (we'll base64 it)
 *  @param {string=} opts.email            optional pre-fill for Stripe page
 *
 *  Throws:
 *    • Error('file_too_large')  — content exceeds 8 MB after base64
 *    • billing errors normalised by billingErrorMessage()
 */
export async function startSinglePurchase({ toolSlug, params, currency, fileName, fileMime, content, email }) {
  if (!toolSlug || typeof toolSlug !== 'string') throw new Error('bad_slug');
  if (!fileName || !fileMime) throw new Error('bad_file');
  const cur = (currency || 'usd').toLowerCase();
  if (!SINGLE_PURCHASE_AMOUNT_CENTS[cur]) throw new Error('bad_currency');

  const file_bytes_base64 = toBase64(content);
  // Sanity check matching the worker's default 8 MB cap. We bail
  // client-side so the user gets a clearer error instead of a 413.
  // base64 expands ~33%, so 8 MB decoded ≈ 10.7 MB encoded.
  if (file_bytes_base64.length > 11 * 1024 * 1024) {
    throw new Error('file_too_large');
  }

  const r = await api('/api/billing/single-purchase', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tool_slug: toolSlug,
      params: params || {},
      currency: cur,
      file_name: fileName,
      file_mime: fileMime,
      file_bytes_base64,
      email: email || null,
    }),
  });
  if (!r?.url) throw new Error('No checkout URL returned');
  window.location.assign(r.url);
}

/** Fetch the single-purchase status by Stripe Checkout Session ID.
 *  Called from /thanks/single in a polling loop until status='succeeded'
 *  (at which point `download_token` is populated). The endpoint never
 *  exposes the file payload itself — only metadata + the token. */
export async function getSinglePurchaseBySession(sessionId) {
  if (!sessionId || !sessionId.startsWith('cs_')) throw new Error('bad_session');
  return api(`/api/billing/single-purchase/by-session/${encodeURIComponent(sessionId)}`);
}

/** Trigger the actual file download for a paid single purchase.
 *  Opens the binary response as a browser download via blob URL.
 *  The worker returns Content-Disposition: attachment, so we honour
 *  the filename it provides (set at purchase time). */
export async function downloadSinglePurchase(token, fallbackName = 'makercutt-download') {
  if (!token || !/^[a-f0-9]{32,128}$/i.test(token)) {
    throw new Error('bad_token');
  }
  // We use raw fetch here (not the api() wrapper) because the response
  // is binary, not JSON — api() would try to .json() it.
  const base = apiBase || '';
  const res = await fetch(`${base}/api/billing/single-purchase/${encodeURIComponent(token)}/download`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    let code = '';
    try {
      const j = await res.json();
      code = j?.detail?.code || '';
    } catch (_) { /* binary */ }
    const err = new Error(code || `download_failed_${res.status}`);
    err.status = res.status;
    throw err;
  }
  // Honour the worker's Content-Disposition filename when present.
  let name = fallbackName;
  const cd = res.headers.get('content-disposition') || '';
  const m = cd.match(/filename="?([^"]+)"?/i);
  if (m && m[1]) name = m[1];

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the browser a tick to start the download before revoking
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return { name, size: blob.size };
}

/** Translate backend billing error codes into localised messages.
 *
 *  Robust against two common pitfalls:
 *    1. The i18n `t()` function in this app returns the KEY itself when
 *       the translation is missing (see src/i18n.js line ~97), so a
 *       naive `t('billingError') || fallback` never reaches the
 *       fallback. We wrap `t()` in `tr()` which treats `result === key`
 *       as "missing" and substitutes the inline PT string instead.
 *    2. Network/edge failures (e.g. Cloudflare 502 during cold start)
 *       surface as `Error("HTTP 502")` — we detect 5xx and show a
 *       "try again" message instead of the generic billing copy. */
export function billingErrorMessage(err, t) {
  const msg = err?.message || String(err || '');
  // Helper: translate, but swap the i18n key back to a usable string
  // when the dictionary doesn't have that key registered.
  const tr = (key, fallback) => {
    const out = t?.(key);
    return (out && out !== key) ? out : fallback;
  };
  // FastAPI HTTPException details surface as JSON strings sometimes —
  // try to extract the {code, message} payload.
  let code = '';
  try {
    const m = msg.match(/\{[\s\S]*\}/);
    if (m) {
      const j = JSON.parse(m[0].replace(/'/g, '"'));
      if (typeof j === 'object' && j) {
        code = j.code || (j.detail && j.detail.code) || '';
      }
    }
  } catch (_) { /* noop */ }
  if (!code) {
    if (/stripe_disabled/.test(msg))     code = 'stripe_disabled';
    else if (/price_missing/.test(msg))  code = 'price_missing';
    else if (/amount_too_low/.test(msg)) code = 'amount_too_low';
    else if (/no_customer/.test(msg))    code = 'no_customer';
    else if (/bad_currency/.test(msg))   code = 'bad_currency';
    // Single-purchase specific codes
    else if (/file_too_large/.test(msg)) code = 'file_too_large';
    else if (/bad_file_mime/.test(msg))  code = 'bad_file_mime';
    else if (/bad_file_name/.test(msg))  code = 'bad_file_name';
    else if (/bad_token/.test(msg))      code = 'bad_token';
    else if (/expired/.test(msg))        code = 'token_expired';
    else if (/max_downloads/.test(msg))  code = 'download_limit';
    else if (/stripe_error/.test(msg))   code = 'stripe_error';
    else if (/db_error/.test(msg))       code = 'db_error';
    else if (/empty_file/.test(msg))     code = 'empty_file';
    else if (/401/.test(msg))            code = 'unauthorized';
    // 5xx → almost always a transient edge/cold-start blip on
    // Cloudflare Workers. Asking the user to retry usually fixes it.
    else if (/\b5\d\d\b/.test(msg))      code = 'server_busy';
    else if (/Failed to fetch|NetworkError|TypeError: fetch/i.test(msg)) code = 'network';
  }
  const map = {
    stripe_disabled:  tr('billingStripeDisabled', 'Pagamentos temporariamente indisponíveis.'),
    price_missing:    tr('billingPriceMissing',   'Plano selecionado não está configurado.'),
    amount_too_low:   tr('billingAmountTooLow',   'Valor mínimo não atingido.'),
    no_customer:      tr('billingNoCustomer',     'Você ainda não tem assinatura ativa.'),
    bad_currency:     tr('billingBadCurrency',    'Moeda não suportada.'),
    file_too_large:   tr('billingFileTooLarge',   'Arquivo grande demais para a compra avulsa (máx. 8 MB).'),
    bad_file_mime:    tr('billingBadFileMime',    'Formato de arquivo não permitido para a compra avulsa.'),
    bad_file_name:    tr('billingBadFileName',    'Nome de arquivo inválido.'),
    empty_file:       tr('billingEmptyFile',      'O arquivo gerado está vazio. Ajuste os parâmetros e tente de novo.'),
    stripe_error:     tr('billingStripeError',    'O provedor de pagamento recusou a operação. Tente novamente em instantes.'),
    db_error:         tr('billingDbError',        'Não foi possível registrar a compra. Tente novamente em instantes.'),
    bad_token:        tr('billingBadToken',       'Link de download inválido. Verifique o e-mail enviado.'),
    token_expired:    tr('billingTokenExpired',   'Este link de download expirou (válido por 24 h).'),
    download_limit:   tr('billingDownloadLimit',  'Este download já foi usado o número máximo de vezes (5).'),
    unauthorized:     tr('billingUnauthorized',   'Faça login para continuar.'),
    server_busy:      tr('billingServerBusy',     'O servidor está ocupado. Tente novamente em alguns segundos.'),
    network:          tr('billingNetwork',        'Sem conexão com o servidor. Verifique sua internet e tente de novo.'),
  };
  return map[code] || tr('billingError', 'Não foi possível concluir a operação. Tente novamente em instantes.');
}

/** Pretty-print a cents+currency pair for the UI. Always returns a
 *  string formatted in the donor's currency, never auto-converts. */
export function formatAmount(cents, currency) {
  const major = (cents || 0) / 100;
  if ((currency || '').toLowerCase() === 'brl') {
    return `R$ ${major.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── AI credit packs ─────────────────────────────────────────────────
//
// Pre-paid balance the user can spend on any AI feature (assistant /
// vectorize / halftone / neon / dxf-gen / image-enhance) after their
// monthly tier quota runs out. Companion to the subscription tiers,
// for users who don't want to commit monthly.
//
// Pack catalogue (must mirror worker/src/lib/ai-credits.ts CREDIT_PACKS):
//
//    pack id  │ credits │  USD  │  EUR  │   BRL
//   ─────────┼─────────┼───────┼───────┼─────────
//    small   │     10  │ $1.99 │ €1.99 │ R$ 9,90
//    medium  │     50  │ $7.99 │ €7.99 │ R$ 39,00
//    large   │    200  │$24.99 │€24.99 │ R$ 119,00

export const AI_CREDIT_PACKS = {
  small:  { credits: 10,  prices: { usd: 199,  eur: 199,  brl: 990   } },
  medium: { credits: 50,  prices: { usd: 799,  eur: 799,  brl: 3900  } },
  large:  { credits: 200, prices: { usd: 2499, eur: 2499, brl: 11900 } },
};

/** Pick the right credit-pack currency for the current user — same
 *  rules as single-purchase (BRL for pt, EUR for fr/de/it/es/nl, USD
 *  otherwise). Exposed so the buy-credits modal can default sensibly. */
export function pickAiCreditsCurrency({ lang, currency } = {}) {
  return pickSinglePurchaseCurrency({ lang, currency });
}

/** Fetch the caller's current AI credit balance. Returns 0 on any
 *  failure so the UI fails-open (the user can still see the upsell
 *  button — we never hide payment paths because of a network blip). */
export async function getAiCreditsBalance() {
  try {
    const r = await api('/api/billing/ai-credits/balance');
    return {
      balance:         Number(r?.balance ?? 0),
      lifetime_bought: Number(r?.lifetime_bought ?? 0),
      lifetime_spent:  Number(r?.lifetime_spent ?? 0),
    };
  } catch (_) {
    return { balance: 0, lifetime_bought: 0, lifetime_spent: 0 };
  }
}

/** Start a credit-pack checkout — redirects to Stripe.
 *  @param {Object} opts
 *  @param {'small'|'medium'|'large'} opts.pack
 *  @param {'usd'|'eur'|'brl'} opts.currency
 */
export async function startAiCreditsCheckout({ pack, currency }) {
  if (!AI_CREDIT_PACKS[pack]) throw new Error('bad_pack');
  const cur = (currency || 'usd').toLowerCase();
  if (!AI_CREDIT_PACKS[pack].prices[cur]) throw new Error('bad_currency');

  const r = await api('/api/billing/ai-credits', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pack, currency: cur }),
  });
  if (!r?.url) throw new Error('No checkout URL returned');
  window.location.assign(r.url);
}

/** Poll the AI-credit purchase status on the /account/credits page. */
export async function getAiCreditsBySession(sessionId) {
  if (!sessionId || !sessionId.startsWith('cs_')) throw new Error('bad_session');
  return api(`/api/billing/ai-credits/by-session/${encodeURIComponent(sessionId)}`);
}

// Re-export apiBase so dev tools can build absolute URLs when needed.
export { apiBase };
